import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Wrench, Shield, Users, BarChart2 } from 'lucide-react';
import { useAdminAuth } from '../context/Adminauthcontext';
import { useTheme } from '../context/ThemeContext';
import { Sun, Moon } from 'lucide-react';
import './AdminLoginPage.css';

const FEATURES = [
  { icon: <BarChart2 size={18} />, text: 'Real-time dashboard & analytics' },
  { icon: <Users size={18} />,     text: 'Mechanic & appointment management' },
  { icon: <Shield size={18} />,    text: 'Billing, inventory & service tracker' },
];

export default function AdminLoginPage() {
  const { login }           = useAdminAuth();
  const { theme, toggleTheme } = useTheme();
  const [form, setForm]     = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]  = useState(false);
  const [error, setError]      = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(form.email, form.password);
    setLoading(false);
    if (!result.success) setError(result.error);
  };

  return (
    <div className="admin-login">
      {/* Left panel — branding */}
      <div className="admin-login__left">
        <div className="admin-login__left-content">
          <div className="admin-login__brand">
            <div className="admin-login__brand-icon"><Wrench size={22} /></div>
            <span className="admin-login__brand-name">AutoElite</span>
          </div>
          <h1 className="admin-login__headline">
            Your Workshop.<br />
            <span>Fully in Control.</span>
          </h1>
          <p className="admin-login__sub">
            The all-in-one admin platform for managing appointments, mechanics, inventory, and billing — built for modern service centres.
          </p>
          <div className="admin-login__features">
            {FEATURES.map(({ icon, text }) => (
              <div key={text} className="admin-login__feature">
                <div className="admin-login__feature-icon">{icon}</div>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="admin-login__left-footer">
          <p>© {new Date().getFullYear()} AutoElite. All rights reserved.</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="admin-login__right">
        {/* Theme toggle top right */}
        <button className="admin-login__theme-btn" onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
        </button>

        <div className="admin-login__form-wrap">
          <div className="admin-login__form-header">
            <h2>Admin Sign In</h2>
            <p>Enter your credentials to access the dashboard.</p>
          </div>

          {error && (
            <div className="admin-login__error">
              <Shield size={14} /> {error}
            </div>
          )}

          <form className="admin-login__form" onSubmit={handleSubmit}>
            <div className="admin-login__field">
              <label className="label">Email Address</label>
              <div className="admin-login__input-wrap">
                <Mail size={15} className="admin-login__input-icon" />
                <input
                  className="input admin-login__input"
                  type="email"
                  placeholder="admin@autoelite.in"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="admin-login__field">
              <label className="label">Password</label>
              <div className="admin-login__input-wrap">
                <Lock size={15} className="admin-login__input-icon" />
                <input
                  className="input admin-login__input"
                  type={showPass ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                />
                <button
                  type="button"
                  className="admin-login__pass-toggle"
                  onClick={() => setShowPass(v => !v)}
                  tabIndex={-1}
                >
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <button className="btn btn-primary admin-login__submit" type="submit" disabled={loading}>
              {loading
                ? <span className="admin-login__spinner" />
                : <><ArrowRight size={16} /> Sign In to Dashboard</>
              }
            </button>
          </form>

          <p className="admin-login__note">
            <Shield size={12} /> Secure admin access only. Unauthorised access is prohibited.
          </p>
        </div>
      </div>
    </div>
  );
}